import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Empty,
  Input,
  Menu,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  message as toast,
} from 'antd';
import {
  BellOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  LockOutlined,
  ReloadOutlined,
  SendOutlined,
  SoundOutlined,
  StopOutlined,
  SwapOutlined,
  TeamOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  acquireConversationLock,
  appendInternalNote,
  assignConversation,
  changeConversationStatus,
  claimConversation,
  getConversationEvents,
  getConversationProjects,
  getDeskAgents,
  getDeskCapabilities,
  getDeskQueues,
  getInbox,
  getInboxChanges,
  getInboxCounters,
  getMessagesAfter,
  markConversationRead,
  releaseConversationLock,
  replyToConversation,
  resolveConversation,
  setConversationPriority,
  transferConversation,
  updateAgentPresence,
  type AgentPresenceState,
  type ConversationAgentSummary,
  type ConversationAssignmentEvent,
  type ConversationDeskCapabilities,
  type ConversationInboxCounters,
  type ConversationInboxView,
  type ConversationMessage,
  type ConversationPriority,
  type ConversationProjectAccess,
  type ConversationQueue,
  type ConversationThread,
  type ConversationType,
} from '@/api/conversationApi';
import { MessageBubble, ThreadListItem, formatTime, formatWaiting } from './ServiceDeskParts';
import { useDeskNotifications } from './useDeskNotifications';

const { Text, Title } = Typography;

/** 中文：分頁可見時的輪詢間隔。English: Poll interval while the tab is visible. */
const ACTIVE_POLL_MS = 3_000;
/** 中文：分頁隱藏時降頻，避免無謂查詢。English: Reduced cadence while hidden to avoid pointless queries. */
const BACKGROUND_POLL_MS = 15_000;
/** 中文：軟鎖 120 秒，提早續約。English: The soft lock lasts 120s; renew early. */
const LOCK_HEARTBEAT_MS = 60_000;
const PRESENCE_HEARTBEAT_MS = 60_000;
const DRAFT_PREFIX = 'wems_crm_desk_draft_';

const VIEWS: ConversationInboxView[] = ['awaiting', 'mine', 'unassigned', 'all', 'resolved'];

function isClosed(thread: ConversationThread): boolean {
  return thread.status === 'resolved' || thread.status === 'closed';
}

/**
 * 中文：客戶端的視圖判定必須與伺服器語意一致，否則增量輪詢合併進來的 thread 會出現在錯誤的視圖。
 * English: Client-side view matching must mirror the server semantics, otherwise threads merged in by the
 *          incremental poll would surface under the wrong view.
 */
function matchesView(
  thread: ConversationThread,
  view: ConversationInboxView,
  agentSubjectId: string
): boolean {
  const closed = isClosed(thread);
  switch (view) {
    case 'mine':
      return !closed && thread.assignedAgentId === agentSubjectId;
    case 'unassigned':
      return !closed && !thread.assignedAgentId;
    case 'awaiting':
      return !closed && thread.awaitingReply;
    case 'resolved':
      return closed;
    default:
      return !closed;
  }
}

/**
 * 中文：合併訊息時一律以 messageId 去重並依 revision 排序。
 *       送出回覆採樂觀顯示，但背景輪詢可能在 POST 回應之前就把同一則訊息抓回來；
 *       任何一條寫入路徑只要少了去重，同一則就會在畫面上出現兩次（資料庫仍只有一筆）。
 *       因此所有寫入 messages 的地方都必須走這個函式，不得直接 concat。
 * English: Merging messages always de-duplicates by messageId and orders by revision.
 *       Replies render optimistically, but a background poll can pull the same message in before the POST resolves;
 *       any append path missing the de-duplication makes one message appear twice on screen while the database
 *       still holds exactly one. Every write to `messages` must go through this helper, never a bare concat.
 */
function mergeMessages(
  current: ConversationMessage[],
  incoming: ConversationMessage[]
): ConversationMessage[] {
  const byId = new Map(current.map((item) => [item.messageId, item]));
  for (const item of incoming) byId.set(item.messageId, item);
  return [...byId.values()].sort(
    (a, b) =>
      a.sourceRevision - b.sourceRevision || a.messageId.localeCompare(b.messageId)
  );
}

function compareThreads(a: ConversationThread, b: ConversationThread): number {
  if (a.awaitingReply !== b.awaitingReply) return a.awaitingReply ? -1 : 1;
  const left = new Date(a.lastMessageAt ?? a.updatedAt).getTime();
  const right = new Date(b.lastMessageAt ?? b.updatedAt).getTime();
  // 中文：待回覆的等最久排最前面；其餘依最近更新排序。
  // English: Among awaiting threads the longest wait comes first; the rest sort by most recent update.
  return a.awaitingReply ? left - right : right - left;
}

export default function ConversationManagementPage() {
  const { t } = useTranslation();

  const [capabilities, setCapabilities] = useState<ConversationDeskCapabilities | null>(null);
  const [projects, setProjects] = useState<ConversationProjectAccess[]>([]);
  const [queues, setQueues] = useState<ConversationQueue[]>([]);
  const [agents, setAgents] = useState<ConversationAgentSummary[]>([]);
  const [counters, setCounters] = useState<ConversationInboxCounters | null>(null);

  const [view, setView] = useState<ConversationInboxView>('awaiting');
  const [projectId, setProjectId] = useState<string>();
  const [queueId, setQueueId] = useState<string>();
  const [conversationType, setConversationType] = useState<ConversationType>();
  const [search, setSearch] = useState('');

  const [threads, setThreads] = useState<ConversationThread[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [events, setEvents] = useState<ConversationAssignmentEvent[]>([]);

  const [reply, setReply] = useState('');
  const [composeMode, setComposeMode] = useState<'public' | 'internal'>('public');
  const [resolution, setResolution] = useState('');
  const [approveReuse, setApproveReuse] = useState(false);
  const [presence, setPresence] = useState<AgentPresenceState>('online');

  const [lockHolder, setLockHolder] = useState<string | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferAgent, setTransferAgent] = useState<string>();
  const [transferQueue, setTransferQueue] = useState<string>();
  const [transferNote, setTransferNote] = useState('');

  const [loadingThreads, setLoadingThreads] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => Date.now());

  const cursorRef = useRef<string | null>(null);
  const selectedIdRef = useRef<string | null>(null);
  const threadsRef = useRef<ConversationThread[]>([]);
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const pollingRef = useRef(false);
  // 中文：送出中的旗標用 ref 而非 state。setSubmitting 是非同步的，連續兩次 Enter 可能都通過 state 檢查
  //       而送出兩次；ref 是同步的，能真正擋住重複送出。
  // English: The in-flight flag is a ref, not state. setSubmitting is asynchronous, so two rapid Enter presses can
  //       both pass a state check and post twice; a ref updates synchronously and actually blocks the second send.
  const sendingRef = useRef(false);

  const notifications = useDeskNotifications(counters?.unreadTotal ?? 0);
  const notifyRef = useRef(notifications.notify);
  notifyRef.current = notifications.notify;

  const agentSubjectId = capabilities?.agentSubjectId ?? '';
  const selected = useMemo(
    () => threads.find((thread) => thread.conversationId === selectedId) ?? null,
    [threads, selectedId]
  );

  const projectName = useCallback(
    (id: string) => projects.find((project) => project.projectId === id)?.displayName ?? id,
    [projects]
  );

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    threadsRef.current = threads;
  }, [threads]);

  // ---------------------------------------------------------------------------------------------------------
  // 中文：開機載入靜態資料。English: Bootstrap static data.
  // ---------------------------------------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [access, allowedProjects, queueList, agentList] = await Promise.all([
          getDeskCapabilities(),
          getConversationProjects(),
          getDeskQueues(),
          getDeskAgents(),
        ]);
        if (cancelled) return;
        setCapabilities(access);
        setProjects(allowedProjects);
        setQueues(queueList);
        setAgents(agentList);
      } catch {
        if (!cancelled) setError(t('conversations.load_failed'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  // ---------------------------------------------------------------------------------------------------------
  // 中文：視圖或篩選變更時重載清單，並重置增量游標。
  // English: Reload the list and reset the incremental cursor whenever the view or filters change.
  // ---------------------------------------------------------------------------------------------------------
  const loadInbox = useCallback(async () => {
    setLoadingThreads(true);
    setError('');
    try {
      const page = await getInbox({
        view,
        projectId,
        queueId,
        conversationType,
        search: search.trim() || undefined,
        limit: 100,
      });
      setThreads([...page.items].sort(compareThreads));
      cursorRef.current = null;
      setSelectedId((current) =>
        current && page.items.some((item) => item.conversationId === current)
          ? current
          : page.items[0]?.conversationId ?? null
      );
    } catch {
      setError(t('conversations.load_failed'));
    } finally {
      setLoadingThreads(false);
    }
  }, [conversationType, projectId, queueId, search, t, view]);

  useEffect(() => {
    void loadInbox();
  }, [loadInbox]);

  // ---------------------------------------------------------------------------------------------------------
  // 中文：增量輪詢。以遞迴 setTimeout 取代 setInterval，確保上一次請求完成前不會再送出，
  //       避免網路變慢時堆疊請求。分頁隱藏時自動降頻，重新取得焦點立即補拉一次。
  // English: Incremental polling. A recursive setTimeout replaces setInterval so a new request never starts before the
  //       previous one finishes, preventing pile-up on a slow network. It slows down while hidden and catches up
  //       immediately on refocus.
  // ---------------------------------------------------------------------------------------------------------
  const pollOnce = useCallback(async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    try {
      const [changes, nextCounters] = await Promise.all([
        getInboxChanges(cursorRef.current, projectId, queueId),
        getInboxCounters(),
      ]);
      cursorRef.current = changes.serverTime;
      setCounters(nextCounters);

      if (changes.changed.length > 0) {
        const previous = new Map(
          threadsRef.current.map((thread) => [thread.conversationId, thread])
        );

        setThreads((current) => {
          const merged = new Map(current.map((thread) => [thread.conversationId, thread]));
          for (const thread of changes.changed) {
            if (matchesView(thread, view, agentSubjectId)) {
              merged.set(thread.conversationId, thread);
            } else {
              merged.delete(thread.conversationId);
            }
          }
          return [...merged.values()].sort(compareThreads);
        });

        for (const thread of changes.changed) {
          const before = previous.get(thread.conversationId);
          const isNewCustomerMessage =
            thread.awaitingReply &&
            thread.hasUnread &&
            thread.unreadHintRevision > (before?.unreadHintRevision ?? 0);
          const relevant =
            !thread.assignedAgentId || thread.assignedAgentId === agentSubjectId;
          if (
            isNewCustomerMessage &&
            relevant &&
            thread.conversationId !== selectedIdRef.current
          ) {
            notifyRef.current(
              t('conversations.notify_title'),
              `${thread.title || thread.ownerSubjectId}: ${thread.lastMessagePreview ?? ''}`,
              thread.conversationId
            );
          }
        }
      }
    } catch {
      // 中文：輪詢失敗不覆蓋畫面上的錯誤提示，也不清空清單；下一次輪詢會自行恢復。
      // English: A failed poll neither overwrites the on-screen error nor clears the list; the next poll recovers.
    } finally {
      pollingRef.current = false;
    }
  }, [agentSubjectId, projectId, queueId, t, view]);

  useEffect(() => {
    if (!capabilities?.canRead) return undefined;
    let timer: number | undefined;
    let stopped = false;

    const schedule = () => {
      if (stopped) return;
      const delay = document.visibilityState === 'visible' ? ACTIVE_POLL_MS : BACKGROUND_POLL_MS;
      timer = window.setTimeout(async () => {
        await pollOnce();
        schedule();
      }, delay);
    };

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      window.clearTimeout(timer);
      void pollOnce().then(schedule);
    };

    void pollOnce().then(schedule);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [capabilities?.canRead, pollOnce]);

  // 中文：等待時長需要每秒重繪。English: Waiting timers need a one-second repaint.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  // 中文：上線狀態心跳；心跳停止超過 3 分鐘後端即視為離線。
  // English: Presence heartbeat; the server treats more than three minutes without one as offline.
  useEffect(() => {
    if (!capabilities?.canRead) return undefined;
    void updateAgentPresence(presence).catch(() => undefined);
    const timer = window.setInterval(() => {
      void updateAgentPresence(presence).catch(() => undefined);
    }, PRESENCE_HEARTBEAT_MS);
    return () => window.clearInterval(timer);
  }, [capabilities?.canRead, presence]);

  // ---------------------------------------------------------------------------------------------------------
  // 中文：選取對話後載入訊息、取得軟鎖、標記已讀、還原草稿。
  // English: On selection load messages, take the soft lock, mark as read, and restore the draft.
  // ---------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      setEvents([]);
      setLockHolder(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingMessages(true);
    setReply(localStorage.getItem(DRAFT_PREFIX + selectedId) ?? '');
    setComposeMode('public');

    void (async () => {
      try {
        const [page, eventList] = await Promise.all([
          getMessagesAfter(selectedId, 0, 100),
          getConversationEvents(selectedId).catch(() => []),
        ]);
        if (cancelled) return;
        setMessages(page.items);
        setEvents(eventList);
      } catch {
        if (!cancelled) setError(t('conversations.messages_failed'));
      } finally {
        if (!cancelled) setLoadingMessages(false);
      }
    })();

    void (async () => {
      try {
        const lock = await acquireConversationLock(selectedId, false);
        if (!cancelled) setLockHolder(lock.acquired ? null : lock.lockedBy);
      } catch {
        if (!cancelled) setLockHolder(null);
      }
    })();

    const conversationId = selectedId;
    return () => {
      cancelled = true;
      void releaseConversationLock(conversationId).catch(() => undefined);
    };
  }, [selectedId, t]);

  // 中文：開啟中的對話有新 revision 時只補抓增量，不再全量重抓。
  // English: A new revision on the open thread fetches only the delta instead of re-reading everything.
  useEffect(() => {
    if (!selected || messages.length === 0) return;
    const loaded = messages.reduce((max, item) => Math.max(max, item.sourceRevision), 0);
    if (selected.sourceRevision <= loaded) return;
    let cancelled = false;
    void (async () => {
      try {
        const page = await getMessagesAfter(selected.conversationId, loaded, 100);
        if (cancelled || page.items.length === 0) return;
        setMessages((current) => mergeMessages(current, page.items));
      } catch {
        // 中文：增量失敗時保留既有訊息，下一次 revision 變動會再試。
        // English: On failure keep what is shown; the next revision change retries.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [messages, selected]);

  // 中文：閱讀中即標記已讀，未讀紅點與分頁標題會在下一次輪詢自動歸零。
  // English: Reading marks it read; the unread dot and tab badge clear on the next poll.
  useEffect(() => {
    if (!selected || !selected.hasUnread) return;
    void markConversationRead(selected.conversationId, selected.unreadHintRevision).catch(
      () => undefined
    );
  }, [selected]);

  // 中文：捲到底部顯示最新訊息。English: Scroll to the newest message.
  useEffect(() => {
    const node = transcriptRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages]);

  // 中文：撰寫中續約軟鎖，離開或送出後由 effect 清理釋放。
  // English: Renew the soft lock while composing; leaving or sending releases it in the cleanup.
  useEffect(() => {
    if (!selectedId || lockHolder) return undefined;
    const timer = window.setInterval(() => {
      void acquireConversationLock(selectedId, false).catch(() => undefined);
    }, LOCK_HEARTBEAT_MS);
    return () => window.clearInterval(timer);
  }, [lockHolder, selectedId]);

  // 中文：草稿自動保存，換頁或當機都不會遺失已打的字。
  // English: Drafts autosave so a navigation or crash never loses typed text.
  useEffect(() => {
    if (!selectedId) return;
    if (reply.trim()) localStorage.setItem(DRAFT_PREFIX + selectedId, reply);
    else localStorage.removeItem(DRAFT_PREFIX + selectedId);
  }, [reply, selectedId]);

  // ---------------------------------------------------------------------------------------------------------
  // 中文：操作。English: Actions.
  // ---------------------------------------------------------------------------------------------------------
  const applyThread = useCallback((updated: ConversationThread) => {
    setThreads((current) => {
      const merged = new Map(current.map((thread) => [thread.conversationId, thread]));
      merged.set(updated.conversationId, updated);
      return [...merged.values()].sort(compareThreads);
    });
  }, []);

  const runAction = useCallback(
    async (action: () => Promise<unknown>, successKey: string, failureKey: string) => {
      setSubmitting(true);
      try {
        await action();
        toast.success(t(successKey));
      } catch (actionError) {
        const status = (actionError as { response?: { status?: number } }).response?.status;
        toast.error(status === 409 ? t('conversations.already_assigned') : t(failureKey));
      } finally {
        setSubmitting(false);
      }
    },
    [t]
  );

  const sendReply = async () => {
    const content = reply.trim();
    if (!selected || !content || sendingRef.current) return;
    sendingRef.current = true;
    setSubmitting(true);
    try {
      if (composeMode === 'internal') {
        const note = await appendInternalNote(selected.conversationId, content);
        setMessages((current) => mergeMessages(current, [note.resource]));
        toast.success(t('conversations.note_saved'));
      } else {
        const sent = await replyToConversation(selected.conversationId, content);
        setMessages((current) => mergeMessages(current, [sent.resource]));
        toast.success(t('conversations.reply_saved'));
      }
      setReply('');
      localStorage.removeItem(DRAFT_PREFIX + selected.conversationId);
      await pollOnce();
    } catch (sendError) {
      // 中文：不同的失敗原因對客服的處置完全不同，必須分開告知。原本一律顯示「回覆失敗」，
      //       客服無從得知是被法遵規則擋下、被他人鎖定、還是沒有權限，只能反覆重送。
      //       特別是 422 法遵攔截：內容不改就一定會再失敗，必須明講並保留草稿。
      // English: Different failures need different agent responses, so they must be reported separately. The previous
      //       blanket "reply failed" left the agent unable to tell a compliance block from a lock conflict or a missing
      //       permission, so they just retried. A 422 compliance block in particular will always fail again unless the
      //       wording changes, so it must be stated explicitly and the draft kept.
      const response = (sendError as { response?: { status?: number; data?: { code?: string } } })
        .response;
      const status = response?.status;
      const code = response?.data?.code;
      if (status === 422 || code === 'CONVERSATION_STOCK_COMPLIANCE_BLOCKED') {
        toast.error(t('conversations.compliance_blocked'), 8);
      } else if (status === 409) {
        toast.error(t('conversations.locked_conflict'));
      } else if (status === 403) {
        toast.error(t('conversations.reply_forbidden'));
      } else {
        toast.error(
          composeMode === 'internal'
            ? t('conversations.note_failed')
            : t('conversations.reply_failed')
        );
      }
    } finally {
      sendingRef.current = false;
      setSubmitting(false);
    }
  };

  const closeThread = async () => {
    if (!selected) return;
    await runAction(
      async () => {
        const validUntil = approveReuse
          ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
          : null;
        await resolveConversation(
          selected.conversationId,
          resolution.trim(),
          approveReuse,
          validUntil
        );
        setResolution('');
        setApproveReuse(false);
        await pollOnce();
      },
      'conversations.resolved',
      'conversations.resolve_failed'
    );
  };

  const canReply = Boolean(capabilities?.canReplyOrResolve) && selected !== null && !isClosed(selected);
  const blockedByLock = Boolean(lockHolder) && lockHolder !== agentSubjectId;

  const viewCount = (item: ConversationInboxView): number | undefined => {
    if (!counters) return undefined;
    if (item === 'mine') return counters.mine;
    if (item === 'unassigned') return counters.unassigned;
    if (item === 'awaiting') return counters.awaiting;
    if (item === 'all') return counters.all;
    return undefined;
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>
            {t('conversations.desk_title')}
          </Title>
          <Space size={8} wrap>
            <Text type="secondary">
              {t('conversations.company')}: {capabilities?.companyId ?? '—'}
            </Text>
            {capabilities && (
              <Tooltip title={t('conversations.scope_hint')}>
                <Tag color="blue">
                  {t(`conversations.scope_${capabilities.scopeMode}`)}
                </Tag>
              </Tooltip>
            )}
          </Space>
        </div>
        <Space wrap>
          <Tooltip title={t('conversations.sound_hint')}>
            <Space size={4}>
              <SoundOutlined />
              <Switch
                size="small"
                checked={notifications.soundEnabled}
                onChange={notifications.setSoundEnabled}
              />
            </Space>
          </Tooltip>
          <Tooltip title={t('conversations.desktop_hint')}>
            <Space size={4}>
              <BellOutlined />
              <Switch
                size="small"
                checked={notifications.desktopEnabled}
                disabled={notifications.desktopPermission === 'unsupported'}
                onChange={(value) => void notifications.requestDesktop(value)}
              />
            </Space>
          </Tooltip>
          <Select
            value={presence}
            onChange={setPresence}
            style={{ width: 130 }}
            options={[
              { value: 'online', label: `🟢 ${t('conversations.presence_online')}` },
              { value: 'away', label: `🟡 ${t('conversations.presence_away')}` },
              { value: 'offline', label: `⚪ ${t('conversations.presence_offline')}` },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={() => void loadInbox()}>
            {t('conversations.refresh')}
          </Button>
        </Space>
      </Space>

      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} />}
      {capabilities && !capabilities.canRead && (
        <Alert type="warning" showIcon message={t('conversations.no_permission')} />
      )}

      <Row gutter={12}>
        {/* 第 1 欄：佇列與視圖導覽 / Column 1: queue and view navigation */}
        <Col xs={24} md={6} lg={4}>
          <Card size="small" styles={{ body: { padding: 8 } }}>
            <Menu
              mode="inline"
              selectedKeys={[view]}
              onClick={(item) => setView(item.key as ConversationInboxView)}
              style={{ border: 'none' }}
              items={VIEWS.map((item) => ({
                key: item,
                icon:
                  item === 'awaiting' ? (
                    <ClockCircleOutlined />
                  ) : item === 'mine' ? (
                    <UserAddOutlined />
                  ) : item === 'unassigned' ? (
                    <TeamOutlined />
                  ) : item === 'resolved' ? (
                    <CheckCircleOutlined />
                  ) : (
                    <InboxOutlined />
                  ),
                label: (
                  <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                    <span>{t(`conversations.view_${item}`)}</span>
                    {viewCount(item) !== undefined && (
                      <Badge
                        count={viewCount(item)}
                        showZero={false}
                        color={item === 'awaiting' ? '#cf1322' : '#1677ff'}
                      />
                    )}
                  </Space>
                ),
              }))}
            />
            <Divider style={{ margin: '8px 0' }} />
            <Space direction="vertical" size={8} style={{ width: '100%', padding: 8 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t('conversations.all_projects')}
                value={projectId}
                onChange={setProjectId}
                style={{ width: '100%' }}
                options={projects.map((project) => ({
                  value: project.projectId,
                  label: project.displayName,
                }))}
              />
              <Select
                allowClear
                placeholder={t('conversations.all_queues')}
                value={queueId}
                onChange={setQueueId}
                style={{ width: '100%' }}
                options={queues.map((queue) => ({
                  value: queue.queueId,
                  label: queue.displayName,
                }))}
              />
              <Select
                allowClear
                placeholder={t('conversations.all_types')}
                value={conversationType}
                onChange={setConversationType}
                style={{ width: '100%' }}
                options={[
                  { value: 'dialogue', label: t('conversations.dialogue') },
                  { value: 'support', label: t('conversations.support') },
                ]}
              />
            </Space>
          </Card>
        </Col>

        {/* 第 2 欄：對話列表 / Column 2: thread list */}
        <Col xs={24} md={18} lg={6}>
          <Card
            size="small"
            title={
              <Input.Search
                allowClear
                placeholder={t('conversations.search_placeholder')}
                onSearch={setSearch}
                size="small"
              />
            }
            styles={{ body: { padding: 0 } }}
          >
            <Spin spinning={loadingThreads}>
              <div style={{ maxHeight: 700, overflowY: 'auto' }}>
                {threads.length === 0 ? (
                  <Empty
                    description={t('conversations.no_threads')}
                    style={{ padding: 32 }}
                  />
                ) : (
                  threads.map((thread) => (
                    <ThreadListItem
                      key={thread.conversationId}
                      thread={thread}
                      selected={thread.conversationId === selectedId}
                      now={now}
                      projectName={projectName(thread.projectId)}
                      agentSubjectId={agentSubjectId}
                      onSelect={(item) => setSelectedId(item.conversationId)}
                    />
                  ))
                )}
              </div>
            </Spin>
          </Card>
        </Col>

        {/* 第 3 欄：對話面板 / Column 3: transcript and composer */}
        <Col xs={24} lg={9}>
          <Card
            size="small"
            title={selected?.title || t('conversations.transcript')}
            extra={
              selected && (
                <Space size={4} wrap>
                  {capabilities?.canAssign && !selected.assignedAgentId && (
                    <Button
                      size="small"
                      type="primary"
                      icon={<UserAddOutlined />}
                      loading={submitting}
                      onClick={() =>
                        void runAction(
                          async () => applyThread(await claimConversation(selected.conversationId)),
                          'conversations.claimed',
                          'conversations.claim_failed'
                        )
                      }
                    >
                      {t('conversations.claim')}
                    </Button>
                  )}
                  {capabilities?.canAssign && (
                    <Button
                      size="small"
                      icon={<SwapOutlined />}
                      onClick={() => setTransferOpen(true)}
                    >
                      {t('conversations.transfer')}
                    </Button>
                  )}
                  <Select
                    size="small"
                    value={selected.priority}
                    style={{ width: 96 }}
                    disabled={!capabilities?.canReplyOrResolve}
                    onChange={(value: ConversationPriority) =>
                      void runAction(
                        async () =>
                          applyThread(
                            await setConversationPriority(selected.conversationId, value)
                          ),
                        'conversations.priority_saved',
                        'conversations.priority_failed'
                      )
                    }
                    options={(['low', 'normal', 'high', 'urgent'] as ConversationPriority[]).map(
                      (value) => ({ value, label: t(`conversations.priority_${value}`) })
                    )}
                  />
                  {!isClosed(selected) && capabilities?.canReplyOrResolve && (
                    <Button
                      size="small"
                      onClick={() =>
                        void runAction(
                          async () =>
                            applyThread(
                              await changeConversationStatus(selected.conversationId, 'pending')
                            ),
                          'conversations.status_saved',
                          'conversations.status_failed'
                        )
                      }
                    >
                      {t('conversations.mark_pending')}
                    </Button>
                  )}
                </Space>
              )
            }
          >
            {!selected ? (
              <Empty description={t('conversations.select_thread')} />
            ) : (
              <Spin spinning={loadingMessages}>
                {blockedByLock && (
                  <Alert
                    type="warning"
                    showIcon
                    icon={<LockOutlined />}
                    style={{ marginBottom: 8 }}
                    message={t('conversations.locked_by', { agent: lockHolder })}
                    action={
                      <Button
                        size="small"
                        danger
                        onClick={() =>
                          void runAction(
                            async () => {
                              const lock = await acquireConversationLock(
                                selected.conversationId,
                                true
                              );
                              setLockHolder(lock.acquired ? null : lock.lockedBy);
                            },
                            'conversations.lock_taken',
                            'conversations.lock_failed'
                          )
                        }
                      >
                        {t('conversations.take_over')}
                      </Button>
                    }
                  />
                )}

                <div ref={transcriptRef} style={{ maxHeight: 420, overflowY: 'auto', paddingRight: 8 }}>
                  {messages.length === 0 ? (
                    <Empty description={t('conversations.no_messages')} />
                  ) : (
                    messages.map((item) => <MessageBubble key={item.messageId} message={item} />)
                  )}
                </div>

                <Space direction="vertical" style={{ width: '100%', marginTop: 12 }}>
                  <Segmented
                    size="small"
                    value={composeMode}
                    onChange={(value) => setComposeMode(value as 'public' | 'internal')}
                    options={[
                      { value: 'public', label: t('conversations.compose_public') },
                      {
                        value: 'internal',
                        label: t('conversations.compose_internal'),
                        disabled: !capabilities?.canNote,
                      },
                    ]}
                  />
                  <Input.TextArea
                    value={reply}
                    onChange={(event) => setReply(event.target.value)}
                    placeholder={
                      composeMode === 'internal'
                        ? t('conversations.note_placeholder')
                        : t('conversations.reply_placeholder')
                    }
                    maxLength={20000}
                    autoSize={{ minRows: 3, maxRows: 8 }}
                    disabled={!canReply || blockedByLock}
                    onKeyDown={(event) => {
                      // 中文：Enter 送出、Shift+Enter 換行；輸入法組字中不可攔截，否則中文會被切斷。
                      // English: Enter sends, Shift+Enter breaks the line; never intercept during IME composition or
                      //          Chinese input would be cut off mid-word.
                      if (
                        event.key === 'Enter' &&
                        !event.shiftKey &&
                        !(event.nativeEvent as unknown as { isComposing?: boolean }).isComposing
                      ) {
                        event.preventDefault();
                        void sendReply();
                      }
                    }}
                  />
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('conversations.enter_hint')}
                    </Text>
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      loading={submitting}
                      disabled={!reply.trim() || !canReply || blockedByLock}
                      onClick={() => void sendReply()}
                    >
                      {composeMode === 'internal'
                        ? t('conversations.save_note')
                        : t('conversations.send_reply')}
                    </Button>
                  </Space>

                  {!isClosed(selected) && capabilities?.canReplyOrResolve && (
                    <Card size="small" title={t('conversations.resolve_title')}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Input.TextArea
                          value={resolution}
                          onChange={(event) => setResolution(event.target.value)}
                          placeholder={t('conversations.resolution_placeholder')}
                          maxLength={4000}
                          autoSize={{ minRows: 2, maxRows: 4 }}
                        />
                        <Checkbox
                          checked={approveReuse}
                          disabled={!capabilities.canApproveReuse}
                          onChange={(event) => setApproveReuse(event.target.checked)}
                        >
                          {t('conversations.approve_reuse')}
                        </Checkbox>
                        <Button
                          danger
                          icon={<StopOutlined />}
                          loading={submitting}
                          onClick={() => void closeThread()}
                        >
                          {t('conversations.resolve')}
                        </Button>
                      </Space>
                    </Card>
                  )}
                </Space>
              </Spin>
            )}
          </Card>
        </Col>

        {/* 第 4 欄：客戶與稽核資訊 / Column 4: customer and audit context */}
        <Col xs={24} lg={5}>
          <Card size="small" title={t('conversations.customer_panel')}>
            {!selected ? (
              <Empty description={t('conversations.select_thread')} />
            ) : (
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                <Text type="secondary">{t('conversations.customer')}</Text>
                <Text copyable>{selected.ownerSubjectId}</Text>
                <Text type="secondary">{t('conversations.project')}</Text>
                <Tag color="geekblue">{projectName(selected.projectId)}</Tag>
                <Text type="secondary">{t('conversations.queue')}</Text>
                <Tag>{selected.effectiveQueueId}</Tag>
                <Text type="secondary">{t('conversations.assignee')}</Text>
                <Space wrap>
                  <Tag color={selected.assignedAgentId ? 'green' : 'purple'}>
                    {selected.assignedAgentId ?? t('conversations.unassigned')}
                  </Tag>
                  {capabilities?.canAssign && (
                    <Select
                      size="small"
                      allowClear
                      showSearch
                      style={{ minWidth: 140 }}
                      placeholder={t('conversations.assign_to')}
                      value={selected.assignedAgentId ?? undefined}
                      onChange={(value?: string) =>
                        void runAction(
                          async () =>
                            applyThread(
                              await assignConversation(selected.conversationId, value ?? null)
                            ),
                          'conversations.assigned',
                          'conversations.assign_failed'
                        )
                      }
                      options={agents.map((agent) => ({
                        value: agent.agentSubjectId,
                        label: `${agent.presenceState === 'online' ? '🟢' : agent.presenceState === 'away' ? '🟡' : '⚪'} ${agent.agentSubjectId}`,
                      }))}
                    />
                  )}
                </Space>

                <Divider style={{ margin: '8px 0' }} />
                <Text type="secondary">{t('conversations.stats')}</Text>
                <Text style={{ fontSize: 12 }}>
                  {t('conversations.message_count')}: {selected.messageCount}
                </Text>
                <Text style={{ fontSize: 12 }}>
                  {t('conversations.created_at')}: {formatTime(selected.createdAt)}
                </Text>
                <Text style={{ fontSize: 12 }}>
                  {t('conversations.first_response')}: {formatTime(selected.firstResponseAt)}
                </Text>
                {selected.awaitingReply && (
                  <Text style={{ fontSize: 12, color: '#cf1322' }}>
                    {t('conversations.waiting')}:{' '}
                    {formatWaiting(selected.waitingSince ?? selected.lastMessageAt, now)}
                  </Text>
                )}
                {selected.externalReference && (
                  <Text style={{ fontSize: 12 }}>
                    {t('conversations.external_reference')}: {selected.externalReference}
                  </Text>
                )}

                {events.length > 0 && (
                  <>
                    <Divider style={{ margin: '8px 0' }} />
                    <Text type="secondary">{t('conversations.audit_trail')}</Text>
                    <Timeline
                      style={{ marginTop: 8 }}
                      items={events.slice(-8).map((event) => ({
                        children: (
                          <Space direction="vertical" size={0}>
                            <Text style={{ fontSize: 12 }}>
                              {t(`conversations.event_${event.eventType}`, {
                                defaultValue: event.eventType,
                              })}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {event.eventActorId} · {formatTime(event.createdAt)}
                            </Text>
                          </Space>
                        ),
                      }))}
                    />
                  </>
                )}
              </Space>
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        open={transferOpen}
        title={t('conversations.transfer_title')}
        okText={t('conversations.transfer')}
        confirmLoading={submitting}
        onCancel={() => setTransferOpen(false)}
        onOk={() => {
          if (!selected) return;
          void runAction(
            async () => {
              applyThread(
                await transferConversation(
                  selected.conversationId,
                  transferAgent ?? null,
                  transferQueue ?? null,
                  transferNote.trim() || undefined
                )
              );
              setTransferOpen(false);
              setTransferAgent(undefined);
              setTransferQueue(undefined);
              setTransferNote('');
            },
            'conversations.transferred',
            'conversations.transfer_failed'
          );
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">{t('conversations.transfer_hint')}</Text>
          <Select
            allowClear
            showSearch
            style={{ width: '100%' }}
            placeholder={t('conversations.transfer_agent')}
            value={transferAgent}
            onChange={setTransferAgent}
            options={agents.map((agent) => ({
              value: agent.agentSubjectId,
              label: agent.agentSubjectId,
            }))}
          />
          <Select
            allowClear
            style={{ width: '100%' }}
            placeholder={t('conversations.transfer_queue')}
            value={transferQueue}
            onChange={setTransferQueue}
            options={queues.map((queue) => ({
              value: queue.queueId,
              label: queue.displayName,
            }))}
          />
          <Input.TextArea
            value={transferNote}
            onChange={(event) => setTransferNote(event.target.value)}
            placeholder={t('conversations.transfer_note')}
            maxLength={4000}
            autoSize={{ minRows: 2, maxRows: 4 }}
          />
        </Space>
      </Modal>
    </Space>
  );
}
